<template>
  <nav
    class="calm-nav"
    :class="{ 'calm-nav--collapsed': collapsed }"
    :aria-label="$t('nav.primary')"
    @keydown="onKeydown"
  >
    <template v-for="group in visibleTree">
      <!-- LEVEL 1 — group band / divider -->
      <div class="calm-nav-divider" :key="group.key + '-d'">
        <span class="calm-nav-divider__txt">{{ $t(group.titleKey) }}</span>
        <span class="calm-nav-divider__rule"></span>
      </div>

      <div class="calm-nav-group" :key="group.key + '-g'">
        <template v-for="entry in group.entries">
          <!-- leaf directly under a group (icon-led) -->
          <router-link
            v-if="!entry.section"
            :key="entry.key"
            :to="entry.path"
            custom
            v-slot="{ href, navigate }"
          >
            <a
              :href="href"
              class="calm-nav-leaf calm-nav-leaf--top"
              :class="{ 'is-active': activeKey === entry.key }"
              :data-key="entry.key"
              :aria-current="activeKey === entry.key ? 'page' : null"
              @click="onLeaf($event, navigate)"
            >
              <calm-icon class="calm-nav-leaf__ic" :name="entry.icon || group.icon" :size="17" />
              <span class="calm-nav-leaf__label">{{ $t(entry.labelKey) }}</span>
            </a>
          </router-link>

          <!-- LEVEL 2 — section (accordion) -->
          <div
            v-else
            :key="entry.key"
            class="calm-nav-section"
            :class="{ 'is-open': isOpen(entry.key) }"
          >
            <button
              type="button"
              class="calm-nav-row"
              :class="{ 'is-active-anc': sectionHasActive(entry) }"
              :aria-expanded="isOpen(entry.key) ? 'true' : 'false'"
              :aria-controls="panelId(entry.key)"
              :data-key="entry.key"
              @click="toggleSection(group, entry)"
            >
              <calm-icon class="calm-nav-row__ic" :name="entry.icon" :size="17" />
              <span class="calm-nav-row__label">{{ $t(entry.labelKey) }}</span>
              <calm-icon class="calm-nav-row__caret" name="chevron-down" :size="15" />
            </button>

            <div class="calm-nav-children" :id="panelId(entry.key)">
              <ul class="calm-nav-childlist" role="group" :aria-label="$t(entry.labelKey)">
                <li v-for="leaf in entry.children" :key="leaf.key">
                  <router-link :to="leaf.path" custom v-slot="{ href, navigate }">
                    <a
                      :href="href"
                      class="calm-nav-leaf"
                      :class="{ 'is-active': activeKey === leaf.key }"
                      :data-key="leaf.key"
                      :aria-current="activeKey === leaf.key ? 'page' : null"
                      @click="onLeaf($event, navigate)"
                    >
                      <span class="calm-nav-leaf__bullet"></span>
                      <span class="calm-nav-leaf__label">{{ $t(leaf.labelKey) }}</span>
                    </a>
                  </router-link>
                </li>
              </ul>
            </div>
          </div>
        </template>

        <!-- collapsed-rail fly-out (shown on hover when collapsed) -->
        <div class="calm-nav-railpop" role="menu" :aria-label="$t(group.titleKey)">
          <div class="calm-nav-railpop__title">{{ $t(group.titleKey) }}</div>
          <template v-for="entry in group.entries">
            <a
              v-if="!entry.section"
              :key="'p-' + entry.key"
              class="calm-nav-railpop__leaf"
              :class="{ 'is-active': activeKey === entry.key }"
              @click="navigate(entry)"
            >{{ $t(entry.labelKey) }}</a>
            <template v-else>
              <div class="calm-nav-railpop__sub" :key="'ps-' + entry.key">{{ $t(entry.labelKey) }}</div>
              <a
                v-for="leaf in entry.children"
                :key="'p-' + leaf.key"
                class="calm-nav-railpop__leaf"
                :class="{ 'is-active': activeKey === leaf.key }"
                @click="navigate(leaf)"
              >{{ $t(leaf.labelKey) }}</a>
            </template>
          </template>
        </div>
      </div>
    </template>
  </nav>
</template>

<script>
import NAV_TREE from "@/config/navTree";
import CalmIcon from "./CalmIcon.vue";

const LS_OPEN = "nav.open.sections";

export default {
  name: "calm-sidebar",
  components: { CalmIcon },
  props: {
    collapsed: { type: Boolean, default: false },
  },
  data() {
    let open = [];
    try {
      open = JSON.parse(localStorage.getItem(LS_OPEN) || "[]");
      if (!Array.isArray(open)) open = [];
    } catch (e) {
      open = [];
    }
    return { tree: NAV_TREE, openSections: open };
  },
  computed: {
    user() {
      return this.$store.getters["Auth/getUser"] || {};
    },
    isAdmin() {
      // admin (3) or super admin (99) — matches intent of the old IsAdmin gate
      return this.user.Type == 3 || this.user.Type == 99;
    },
    // gate-filtered tree (admin-only nodes hidden from non-admins), empties pruned
    visibleTree() {
      const ok = (n) => !n.adminOnly || this.isAdmin;
      const out = [];
      for (const g of this.tree) {
        if (!ok(g)) continue;
        const entries = [];
        for (const e of g.entries) {
          if (e.section) {
            if (!ok(e)) continue;
            const children = e.children.filter(ok);
            if (children.length) entries.push({ ...e, children });
          } else if (ok(e)) {
            entries.push(e);
          }
        }
        if (entries.length) out.push({ ...g, entries });
      }
      return out;
    },
    activeKey() {
      const leaf = this.findLeafByPath(this.$route.path);
      return leaf ? leaf.key : null;
    },
  },
  watch: {
    $route() {
      this.expandActiveAncestor();
    },
  },
  created() {
    this.expandActiveAncestor();
  },
  methods: {
    panelId(key) {
      return "calmnav-" + String(key).replace(/[^\w-]/g, "-");
    },
    isOpen(key) {
      return this.openSections.indexOf(key) !== -1;
    },
    persistOpen() {
      try {
        localStorage.setItem(LS_OPEN, JSON.stringify(this.openSections));
      } catch (e) {
        /* ignore */
      }
    },
    findLeafByPath(path) {
      const clean = (path || "").split("?")[0].split("#")[0];
      let exact = null;
      let prefix = null;
      let plen = -1;
      for (const g of this.visibleTree) {
        for (const e of g.entries) {
          const leaves = e.section ? e.children : [e];
          for (const lf of leaves) {
            if (!lf.path) continue;
            if (lf.path === clean) exact = lf;
            else if (clean.indexOf(lf.path + "/") === 0 && lf.path.length > plen) {
              prefix = lf;
              plen = lf.path.length;
            }
          }
        }
      }
      return exact || prefix;
    },
    findPath(key) {
      for (const g of this.visibleTree) {
        for (const e of g.entries) {
          if (!e.section && e.key === key) return { group: g, leaf: e };
          if (e.section) {
            for (const c of e.children) {
              if (c.key === key) return { group: g, section: e, leaf: c };
            }
          }
        }
      }
      return null;
    },
    sectionHasActive(section) {
      return section.children.some((c) => c.key === this.activeKey);
    },
    // multi-open accordion — user controls each section independently;
    // opening a sibling does NOT collapse the previously-open one.
    toggleSection(group, section) {
      if (this.isOpen(section.key)) {
        this.openSections = this.openSections.filter((k) => k !== section.key);
      } else {
        this.openSections = this.openSections.concat(section.key);
      }
      this.persistOpen();
    },
    // auto-expand the active leaf's ancestor section — additive only, never
    // collapses other open sections (multi-open override).
    expandActiveAncestor() {
      const path = this.findPath(this.activeKey);
      if (path && path.section && !this.isOpen(path.section.key)) {
        this.openSections = this.openSections.concat(path.section.key);
        this.persistOpen();
      }
    },
    onLeaf(ev, navigate) {
      this.$emit("navigate");
      navigate(ev);
    },
    navigate(leaf) {
      if (!leaf || !leaf.path) return;
      this.$emit("navigate");
      if (this.$route.path !== leaf.path) this.$router.push(leaf.path).catch(() => {});
    },
    // keyboard: Up/Down move rows, Left/Right collapse/expand a section
    onKeydown(e) {
      const keys = ["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight"];
      if (keys.indexOf(e.key) === -1) return;
      const rows = Array.from(this.$el.querySelectorAll(".calm-nav-row, .calm-nav-leaf"));
      const i = rows.indexOf(document.activeElement);
      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (i < rows.length - 1) rows[i + 1].focus();
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        if (i > 0) rows[i - 1].focus();
      } else if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
        const el = document.activeElement;
        const key = el && el.getAttribute && el.getAttribute("data-key");
        if (!key) return;
        const path = this.findPath(key);
        // on a section header row
        const grp = this.visibleTree.find((g) => g.entries.some((en) => en.section && en.key === key));
        const sec = grp && grp.entries.find((en) => en.section && en.key === key);
        if (sec) {
          e.preventDefault();
          if (e.key === "ArrowRight" && !this.isOpen(key)) this.toggleSection(grp, sec);
          if (e.key === "ArrowLeft" && this.isOpen(key)) this.toggleSection(grp, sec);
        } else if (e.key === "ArrowLeft" && path && path.section) {
          // on a child leaf: focus parent section header
          const btn = this.$el.querySelector('.calm-nav-row[data-key="' + path.section.key + '"]');
          if (btn) { e.preventDefault(); btn.focus(); }
        }
      }
    },
  },
};
</script>

<style scoped>
/* All classes namespaced `calm-nav*` to avoid Bootstrap (.row/.card/.badge) collisions.
   Tokens (--c-*) are provided by the .calm-layout ancestor (DashboardLayout). */
.calm-nav {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 10px 28px;
  font-family: var(--c-font, inherit);
}

/* LEVEL 1 — divider band */
.calm-nav-divider {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 14px 6px 4px;
}
.calm-nav-divider__txt {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em; /* tiny; never uppercase (Thai-safe) */
  color: var(--c-text-soft, #5e6c80);
  white-space: nowrap;
}
.calm-nav-divider__rule {
  height: 1px;
  flex: 1;
  background: var(--c-border, #e4e9f1);
}

.calm-nav-group { position: relative; }

/* LEVEL 2 — section row */
.calm-nav-row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  border-radius: 8px;
  color: var(--c-text-soft, #5e6c80);
  font-size: 13.5px;
  position: relative;
  background: transparent;
  border: 0;
  cursor: pointer;
  transition: background 140ms ease, color 140ms ease;
}
.calm-nav-row:hover { background: var(--c-bg, #f5f7fa); color: var(--c-blue-deep, #2c5282); }
.calm-nav-row:focus-visible { outline: 2px solid var(--c-blue, #3b6ea8); outline-offset: -2px; }
.calm-nav-row__ic { flex: none; }
.calm-nav-row__label {
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.calm-nav-row__caret {
  flex: none;
  color: var(--c-text-mute, #8a96a8);
  transition: transform 140ms ease;
  transform: rotate(-90deg);
}
.calm-nav-section.is-open > .calm-nav-row .calm-nav-row__caret { transform: rotate(0deg); }
.calm-nav-row.is-active-anc { color: var(--c-blue-deep, #2c5282); }

/* accordion */
.calm-nav-children {
  overflow: hidden;
  max-height: 0;
  transition: max-height 220ms ease;
}
.calm-nav-section.is-open > .calm-nav-children { max-height: 640px; }
.calm-nav-childlist {
  list-style: none;
  margin: 2px 0 4px;
  padding: 0;
  position: relative;
}
.calm-nav-childlist::before {
  content: "";
  position: absolute;
  left: 21px;
  top: 2px;
  bottom: 6px;
  width: 1px;
  background: var(--c-border, #e4e9f1);
}

/* LEVEL 3 — leaf */
.calm-nav-leaf {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 7px 10px 7px 30px;
  border-radius: 8px;
  color: var(--c-text-soft, #5e6c80);
  font-size: 13px;
  position: relative;
  text-decoration: none;
  cursor: pointer;
  transition: background 140ms ease, color 140ms ease;
}
.calm-nav-leaf__bullet {
  position: absolute;
  left: 18px;
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: var(--c-border-strong, #c7d1de);
  transition: background 140ms ease;
}
.calm-nav-leaf__label {
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.calm-nav-leaf:hover { background: var(--c-bg, #f5f7fa); color: var(--c-blue-deep, #2c5282); }
.calm-nav-leaf:hover .calm-nav-leaf__bullet { background: var(--c-blue, #3b6ea8); }
.calm-nav-leaf:focus-visible { outline: 2px solid var(--c-blue, #3b6ea8); outline-offset: -2px; }
.calm-nav-leaf--top { padding-left: 10px; }
.calm-nav-leaf--top .calm-nav-leaf__ic { flex: none; }

/* active leaf/row */
.calm-nav-leaf.is-active {
  background: var(--c-blue-soft, #ebf2fa);
  color: var(--c-blue-deep, #2c5282);
  font-weight: 600;
}
.calm-nav-leaf.is-active .calm-nav-leaf__bullet { background: var(--c-blue, #3b6ea8); }
.calm-nav-leaf.is-active::after {
  content: "";
  position: absolute;
  left: 0;
  top: 6px;
  bottom: 6px;
  width: 3px;
  border-radius: 0 3px 3px 0;
  background: var(--c-blue, #3b6ea8);
}

/* ── collapsed icon rail ── */
.calm-nav--collapsed { padding: 10px 8px; align-items: center; }
.calm-nav--collapsed .calm-nav-divider { display: none; }
.calm-nav--collapsed .calm-nav-children { display: none; }
.calm-nav--collapsed .calm-nav-row,
.calm-nav--collapsed .calm-nav-leaf {
  justify-content: center;
  width: 44px;
  padding-left: 0;
  padding-right: 0;
}
.calm-nav--collapsed .calm-nav-row__label,
.calm-nav--collapsed .calm-nav-row__caret,
.calm-nav--collapsed .calm-nav-leaf__label,
.calm-nav--collapsed .calm-nav-leaf__bullet { display: none; }

/* fly-out (hidden unless collapsed + hover) */
.calm-nav-railpop {
  display: none;
  position: absolute;
  left: 52px;
  top: 0;
  min-width: 220px;
  background: var(--c-surface, #fff);
  border: 1px solid var(--c-border, #e4e9f1);
  border-radius: 10px;
  box-shadow: 0 8px 28px rgba(44, 62, 80, 0.16);
  padding: 8px;
  z-index: 50;
}
.calm-nav--collapsed .calm-nav-group:hover > .calm-nav-railpop,
.calm-nav--collapsed .calm-nav-group:focus-within > .calm-nav-railpop { display: block; }
.calm-nav-railpop__title {
  font-size: 11px;
  font-weight: 600;
  color: var(--c-text-soft, #5e6c80);
  padding: 4px 8px 6px;
  border-bottom: 1px solid var(--c-border, #e4e9f1);
  margin-bottom: 4px;
}
.calm-nav-railpop__sub { font-size: 11px; color: var(--c-text-mute, #8a96a8); padding: 6px 8px 2px; }
.calm-nav-railpop__leaf {
  display: block;
  padding: 7px 10px;
  border-radius: 8px;
  color: var(--c-text-soft, #5e6c80);
  font-size: 13px;
  white-space: nowrap;
  cursor: pointer;
  text-decoration: none;
}
.calm-nav-railpop__leaf:hover { background: var(--c-bg, #f5f7fa); color: var(--c-blue-deep, #2c5282); }
.calm-nav-railpop__leaf.is-active { background: var(--c-blue-soft, #ebf2fa); color: var(--c-blue-deep, #2c5282); font-weight: 600; }

@media (prefers-reduced-motion: reduce) {
  .calm-nav * { transition-duration: 0.01ms !important; }
}
</style>
